/**
 * Das Dashboard — `PORTAL-001`.
 *
 * `07` §7.4 stellt sieben Fragen, und die Ansicht beantwortet sie der Reihe
 * nach. Der Satz daneben ist der eigentliche Bauplan: „Keine dekorativen
 * Kennzahlen ohne Handlungswert. Jede Karte führt zu einer gefilterten
 * Arbeitsliste."
 *
 * Deshalb gibt es hier keine Zahl, die nur dasteht. Jede Kachel ist ein
 * Verweis, und was darunter steht, ist bereits die Liste — nicht eine
 * Zusammenfassung davon. Wer auf einem Dashboard erst lesen und dann suchen
 * muss, wo die Sache liegt, benutzt es nach zwei Wochen nicht mehr.
 *
 * **Die Daten kommen aus `@hwc/demo-data`.** Im Zielbild liefert sie die
 * Projektionsschicht (`services/projections`, ADR-021) aus Vikunja-,
 * Nextcloud- und Paperless-Ereignissen. Was hier gerechnet wird, ist derselbe
 * Lesepfad; getauscht wird die Quelle, nicht die Ansicht.
 */

import {
  baldAblaufendeLinks,
  berichteZurPruefung,
  einsaetzeHeute,
  faelligeWartung,
  findeAuftrag,
  findeKunde,
  findePerson,
  offenerEingang,
  ungeleseneNachrichten,
  verspaeteteEinsaetze,
  wartetAufRueckmeldung,
} from "@hwc/demo-data";

import { Hinweis } from "@hwc/ui";

import { useSitzung } from "../../src/daten/Sitzung.js";
import {
  Einsatzabzeichen,
  Kachel,
  Kachelreihe,
  Leer,
  Liste,
  Prioritaetsabzeichen,
  Zeile,
  datum,
  uhrzeit,
  zeitfenster,
  zumAuftrag,
} from "../../src/bereiche/bausteine.js";

export function Dashboard() {
  const { bestand, heute, person } = useSitzung();

  const heutige = einsaetzeHeute(bestand, heute);
  const verspaetet = verspaeteteEinsaetze(bestand, heute);
  const wartend = wartetAufRueckmeldung(bestand);
  const eingang = offenerEingang(bestand);
  const ungelesen = ungeleseneNachrichten(bestand);
  const links = baldAblaufendeLinks(bestand, heute);
  const pruefung = berichteZurPruefung(bestand);
  const wartung = faelligeWartung(bestand, heute);

  const monteure = bestand.personen.filter((p) => p.rollen.includes("technician"));
  const abwesend = monteure.filter((p) => p.verfuegbar === false);

  return (
    <>
      <div className="bereichskopf">
        <h1>Übersicht</h1>
        <span className="bereichskopf__zusatz">
          {person.name} · {datum(heute.toISOString())}
        </span>
      </div>

      {/*
        Ein abwesender Monteur mit zugewiesenen Einsätzen ist der Fall, der
        einen Tag kippt — und der einzige, der oben stehen muss, weil er sich
        nicht von selbst erledigt. Alles andere ist eine Liste weiter unten.
      */}
      {abwesend.length > 0 && (
        <Hinweis titel="Nicht alle Monteure sind verfügbar.">
          {abwesend.map((p) => `${p.name} (${p.abwesenheitsgrund ?? "abwesend"})`).join(", ")}.
          Einsätze, die auf sie eingeplant sind, brauchen eine neue Zuweisung.
        </Hinweis>
      )}

      <Kachelreihe>
        <Kachel titel="Einsätze heute" zahl={heutige.length} zu="/auftraege">
          {monteure.length - abwesend.length} von {monteure.length} Monteuren im Einsatz
        </Kachel>
        <Kachel
          titel="Ohne Rückmeldung"
          zahl={wartend.length + verspaetet.length}
          zu="/auftraege"
          ton={wartend.length + verspaetet.length > 0 ? "warnung" : "neutral"}
        >
          wartet auf Material, Rückfrage oder Start
        </Kachel>
        <Kachel
          titel="Eingang zu prüfen"
          zahl={eingang.length}
          zu="/eingang"
          ton={eingang.length > 0 ? "warnung" : "erfolg"}
        >
          E-Mails, Dokumente, E-Rechnungen
        </Kachel>
        <Kachel
          titel="Berichte zur Freigabe"
          zahl={pruefung.length}
          zu="/auftraege"
          ton={pruefung.length > 0 ? "warnung" : "neutral"}
        >
          Servicebericht wartet auf Büroprüfung
        </Kachel>
        <Kachel titel="Ungelesene E-Mails" zahl={ungelesen.length} zu="/kommunikation">
          noch keinem Auftrag zugeordnet
        </Kachel>
        <Kachel
          titel="MagicLinks laufen ab"
          zahl={links.length}
          zu="/administration"
          ton={links.length > 0 ? "warnung" : "neutral"}
        >
          innerhalb der nächsten drei Tage
        </Kachel>
      </Kachelreihe>

      <h2>Einsätze heute</h2>
      {heutige.length === 0 ? (
        <Leer text="Für heute ist nichts geplant." />
      ) : (
        <Liste>
          {heutige.map((e) => {
            const auftrag = findeAuftrag(bestand, e.auftrag);
            const kunde = auftrag ? findeKunde(bestand, auftrag.kunde) : undefined;
            const monteur = findePerson(bestand, e.monteur);
            return (
              <Zeile
                key={e.ref.opaqueId}
                zu={auftrag ? zumAuftrag(auftrag.nummer) : undefined}
                titel={e.titel}
                unterzeile={
                  <>
                    {kunde?.name ?? "unbekannter Kunde"} ·{" "}
                    {auftrag ? `Auftrag ${auftrag.nummer}` : ""} ·{" "}
                    {monteur?.name ?? "nicht zugewiesen"}
                  </>
                }
                abzeichen={
                  <>
                    <Prioritaetsabzeichen wert={e.prioritaet} />
                    <Einsatzabzeichen zustand={e.zustand} />
                  </>
                }
                rechts={zeitfenster(e.von, e.bis)}
              />
            );
          })}
        </Liste>
      )}

      <h2>Wo eine Rückmeldung fehlt</h2>
      {wartend.length === 0 && verspaetet.length === 0 ? (
        <Leer text="Nichts offen — alle Einsätze sind im erwarteten Zustand." />
      ) : (
        <Liste>
          {verspaetet.map((e) => {
            const auftrag = findeAuftrag(bestand, e.auftrag);
            return (
              <Zeile
                key={`v-${e.ref.opaqueId}`}
                zu={auftrag ? zumAuftrag(auftrag.nummer) : undefined}
                titel={e.titel}
                unterzeile={
                  <>
                    Zeitfenster begann {uhrzeit(e.von)}, noch nicht gestartet ·{" "}
                    {findePerson(bestand, e.monteur)?.name ?? "nicht zugewiesen"}
                  </>
                }
                abzeichen={<Einsatzabzeichen zustand={e.zustand} />}
              />
            );
          })}
          {wartend.map((e) => {
            const auftrag = findeAuftrag(bestand, e.auftrag);
            return (
              <Zeile
                key={`w-${e.ref.opaqueId}`}
                zu={auftrag ? zumAuftrag(auftrag.nummer) : undefined}
                titel={e.titel}
                unterzeile={
                  e.wartetAuf
                    ?? e.hinweis
                    ?? "Abschluss liegt beim Monteur"
                }
                abzeichen={<Einsatzabzeichen zustand={e.zustand} />}
                rechts={datum(e.von)}
              />
            );
          })}
        </Liste>
      )}

      <h2>Eingang, der eine Entscheidung braucht</h2>
      {eingang.length === 0 ? (
        <Leer text="Der Eingangskorb ist leer." />
      ) : (
        <Liste>
          {eingang.slice(0, 5).map((v) => (
            <Zeile
              key={v.ref.opaqueId}
              zu="/eingang"
              titel={v.betreff}
              unterzeile={`${v.absender} · ${datum(v.eingegangen)} ${uhrzeit(v.eingegangen)}`}
              rechts={v.fehler ? "mit Befund" : undefined}
            />
          ))}
        </Liste>
      )}

      {wartung.length > 0 && (
        <>
          <h2>Wartung wird fällig</h2>
          <Liste>
            {wartung.map((p) => (
              <Zeile
                key={p.ref.opaqueId}
                zu="/aufgaben"
                titel={p.titel}
                unterzeile={findeKunde(bestand, p.kunde)?.name ?? ""}
                rechts={`fällig ${datum(p.naechsteFaelligkeit)}`}
              />
            ))}
          </Liste>
        </>
      )}
    </>
  );
}
